import React, { useState } from 'react'
import { Body2, ButtonText, Headline3, Headline5, Subtitle1 } from 'stories/elements/Typography/Text'
import { FeatureGridSlice } from 'interfaces/GeneratedLandingPage'
import { AnalyticsEventLandingPageButtonClick } from 'lib/constants/AnalyticsEventType'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import theme from 'stories/utils/theme'
import useMedia from 'lib/hooks/useMedia'
import { trackEvent } from 'lib/tracking/analytics-service'
import styled, { css } from 'styled-components'
import DynamicButton from './DynamicButton'

const Container = styled.div<{ showFeaturesSideBySide?: boolean }>`
    height: auto;
    min-height: 100%;

    display: grid;
    grid-template-columns: ${({ showFeaturesSideBySide }) => (showFeaturesSideBySide ? '1fr 1fr' : '1fr')};
    align-items: center;
    overflow: hidden;

    @media (max-width: 1050px) {
        grid-template-columns: 1fr;
    }
    @media (max-height: 800px) and (min-width: 751px) {
        min-height: 1000px;
    }
    @media (max-width: 750px) {
        height: auto;
    }
`

const Tabs = styled.div`
    position: absolute;
    top: 0;
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    h5 {
        text-align: center;
        color: ${theme.colors.deepSeaBlue};
        padding: 10px;
    }
`

const Tab = styled.div<{ isSelected?: boolean; activeColor?: string; secondaryTabColor?: string }>`
    cursor: pointer;
    ${({ isSelected, activeColor, secondaryTabColor }) =>
        isSelected
            ? css`
                  border-top: 1px solid ${theme.colors.accentBlue};
                  border-right: 1px solid ${theme.colors.accentBlue};
                  border-left: 1px solid ${theme.colors.accentBlue};
                  background-color: ${activeColor ?? theme.colors.blueIce};
              `
            : css`
                  border-bottom: 1px solid ${theme.colors.accentBlue};
                  background-color: ${secondaryTabColor ?? theme.colors.blueIce};
              `}
`

const Feature = styled.div<{ backgroundColor?: string; showFeaturesSideBySide?: boolean }>`
    background-color: ${({ backgroundColor }) => backgroundColor ?? theme.colors.white};
    height: ${({ showFeaturesSideBySide }) => (showFeaturesSideBySide ? 'auto' : '85vh')};
    width: 100%;
    display: grid;
    height: fit-content;
    grid-template-columns: ${({ showFeaturesSideBySide }) => (showFeaturesSideBySide ? '1fr' : '1fr 1fr')};
    ${({ showFeaturesSideBySide }) =>
        showFeaturesSideBySide
            ? css`
                  grid-template-rows: 1fr 450px;
                  padding-bottom: 100px;
                  .image-wrapper {
                      width: 100%;
                      display: grid;
                      place-content: center;
                  }
                  @media (max-width: 750px) {
                      grid-template-rows: 1fr;
                  }
              `
            : ''}
    .text {
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 0 10% 0 20%;
        gap: 10px;
    }
    @keyframes enter-feature {
        0% {
            opacity: 0;
        }
        100% {
            opacity: 1;
        }
    }
    animation: enter-feature 0.3s linear forwards;

    @media (max-width: 750px) {
        padding-bottom: 0;
        grid-template-columns: 1fr;
        height: auto;
        .text {
            order: 1;
            padding: 25px;
            align-items: center;
            h3,
            h5,
            p {
                text-align: center;
            }
        }
    }
    @media (min-height: 800px) and (max-width: 1250px) {
        height: auto;
        padding: 0;
    }
`

const FeatureImage = styled.img<{ objectFit?: string }>`
    object-fit: ${({ objectFit }) => objectFit ?? 'scale-down'};
    max-width: 100%;
    max-height: 100vh;
    @media (max-width: 750px) {
        order: 0;
        width: 100%;
        height: auto;
    }
`

const ButtonWrapper = styled.div<{ centerAll?: boolean }>`
    ${({ centerAll }) =>
        centerAll
            ? css`
                  display: flex;
                  justify-content: center;
              `
            : ''}
`

interface Props {
    data: FeatureGridSlice['items']
    showFeaturesSideBySide?: boolean
    centerAll?: boolean
    tabs?: string[]
    primaryTabColor?: string
    secondaryTabColor?: string
}

const FullPageFeatureGrid = ({
    data,
    showFeaturesSideBySide,
    centerAll,
    tabs,
    primaryTabColor,
    secondaryTabColor,
}: Props) => {
    const [selectedTabIndex, setSelectedTabIndex] = useState(0)
    const md = useMedia(deviceSize.md)

    const onFeatureGridButtonClick = (eventName: string) =>
        trackEvent(AnalyticsEventLandingPageButtonClick, {
            value: eventName,
        })

    const filterFeature = () => (tabs?.length ? data.filter((item) => item.matching_tab === tabs[selectedTabIndex]) : data)

    return (
        <Container showFeaturesSideBySide={showFeaturesSideBySide}>
            {tabs?.length ? (
                <Tabs>
                    {tabs.map((tab, i) => (
                        <Tab
                            key={tab}
                            activeColor={primaryTabColor}
                            secondaryTabColor={secondaryTabColor}
                            isSelected={selectedTabIndex === i}
                            onClick={() => setSelectedTabIndex(i)}
                        >
                            <Headline5>{tab}</Headline5>
                        </Tab>
                    ))}
                </Tabs>
            ) : null}
            {filterFeature() &&
                filterFeature().map((item) => (
                    <Feature
                        key={`feature-item-${item.title}-${Math.random()}`}
                        backgroundColor={item.background_color}
                        showFeaturesSideBySide={showFeaturesSideBySide}
                    >
                        {item.invert_order ? (
                            <div className="image-wrapper">
                                <FeatureImage
                                    src={item.imagewebp?.url ?? ''}
                                    height={item.imagewebp?.dimensions?.height}
                                    width={item.imagewebp?.dimensions?.width}
                                    alt={item.imagewebp.alt ?? item.title}
                                    objectFit={item?.scaling}
                                    loading="lazy"
                                />
                            </div>
                        ) : null}
                        <div className="text">
                            <Subtitle1
                                color={item.title_color ?? theme.colors.deepSeaBlue}
                                textAlign={centerAll ? 'center' : 'left'}
                            >
                                {item.title}
                            </Subtitle1>
                            <Headline3
                                color={theme.colors.headlineDark}
                                marginBottom={10}
                                textAlign={centerAll ? 'center' : 'left'}
                            >
                                {item.subtitle}
                            </Headline3>
                            {md ? (
                                <Body2
                                    color={theme.colors.actionGray}
                                    fontWeight={400}
                                    fontFamily="var(--font-inter)"
                                    marginBottom={20}
                                    textAlign={centerAll ? 'center' : 'left'}
                                >
                                    {item.text}
                                </Body2>
                            ) : (
                                <Subtitle1
                                    color={theme.colors.actionGray}
                                    fontWeight={400}
                                    fontFamily="var(--font-inter)"
                                    marginBottom={20}
                                    textAlign={centerAll ? 'center' : 'left'}
                                >
                                    {item.text}
                                </Subtitle1>
                            )}
                            {item.button_link && item.button_text ? (
                                <ButtonWrapper
                                    centerAll={centerAll}
                                    onClick={() =>
                                        onFeatureGridButtonClick(item.feature_grid_button_click_analytics_event_value)
                                    }
                                >
                                    <DynamicButton
                                        href={item.button_link ?? ''}
                                        width={280}
                                        type="primary"
                                        color={item.button_color ?? theme.colors.deepSeaBlue}
                                        height={60}
                                        borderRadius={theme.borderRadius.normal}
                                    >
                                        <ButtonText color={theme.colors.white}>{item.button_text}</ButtonText>
                                    </DynamicButton>
                                </ButtonWrapper>
                            ) : null}
                        </div>
                        {!item.invert_order ? (
                            <div className="image-wrapper">
                                <FeatureImage
                                    src={item.imagewebp?.url ?? ''}
                                    height={item.imagewebp?.dimensions?.height}
                                    width={item.imagewebp?.dimensions?.width}
                                    alt={item.imagewebp.alt ?? item.title}
                                    objectFit={item?.scaling}
                                    loading="lazy"
                                />
                            </div>
                        ) : null}
                    </Feature>
                ))}
        </Container>
    )
}

export default FullPageFeatureGrid
